import { cloneDeep } from '@apollo/client/utilities';
import { CompanyEntity, forCreate, forDelete, forUpdate } from '../common/entity';

export enum CompanyConnectionEntityType {
  product_master_data = 'product_master_data',
  stock_locations = 'stock_locations',
  orders = 'orders',
  tasks = 'tasks',
  bins = 'bins',
  bin_status = 'bin_status',
  products = 'products',
  containers = 'containers',
  lots = 'lots',
  product_transactions = 'product_transactions',
  files = 'files',
  tags = 'tags',
  tag_relation = 'tag_relation',
  container_types = 'container_types',
  assets = 'assets',
  asset_transactions = 'asset_transactions',
  handling_units = 'handling_units',
  kits = 'kits',
  kit_templates = 'kit_templates',
  mobile_stock_location_sessions = 'mobile_stock_location_sessions',
  mobile_stock_location_session_history = 'mobile_stock_location_session_history',
  mobile_stock_location_events = 'mobile_stock_location_events',
}

export enum CompanyConnectionState {
  active = 'active',
  incative = 'inactive',
  pending = 'pending',
}

export class CompanyConnection extends CompanyEntity {
  parentId!: string;
  state: CompanyConnectionState = CompanyConnectionState.pending;
  types: CompanyConnectionEntityType[] = [];

  constructor(obj: any) {
    if (!obj.companyId) return;
    super(obj.companyId);
    Object.assign(this, obj);
  }

  forCreate(): CreateCompanyConnectionInput {
    return CreateCompanyConnectionInput.from({ ...this }, CreateCompanyConnectionInput);
  }

  forUpdate(): UpdateCompanyConnectionInput {
    return UpdateCompanyConnectionInput.from({ ...this }, UpdateCompanyConnectionInput);
  }

  forDelete(): DeleteCompanyConnectionInput {
    return DeleteCompanyConnectionInput.from({ ...this }, DeleteCompanyConnectionInput);
  }

  withCompanyId(companyId: string) {
    this.companyId = companyId;
    return cloneDeep(this);
  }

  withParentId(parentId: string) {
    this.parentId = parentId;
    return cloneDeep(this);
  }

  withProductMasterData(enabled: boolean) {
    const types = new Set(this.types);
    enabled
      ? types.add(CompanyConnectionEntityType.product_master_data)
      : types.delete(CompanyConnectionEntityType.product_master_data);
    enabled ? types.add(CompanyConnectionEntityType.files) : types.delete(CompanyConnectionEntityType.files);
    enabled ? types.add(CompanyConnectionEntityType.kits) : types.delete(CompanyConnectionEntityType.kits);
    enabled
      ? types.add(CompanyConnectionEntityType.kit_templates)
      : types.delete(CompanyConnectionEntityType.kit_templates);
    enabled
      ? types.add(CompanyConnectionEntityType.handling_units)
      : types.delete(CompanyConnectionEntityType.handling_units);
    this.types = Array.from(types.values());

    return cloneDeep(this);
  }

  withStockLocations(enabled: boolean) {
    const types = new Set(this.types);

    enabled
      ? types.add(CompanyConnectionEntityType.stock_locations)
      : types.delete(CompanyConnectionEntityType.stock_locations);
    enabled
      ? types.add(CompanyConnectionEntityType.mobile_stock_location_sessions)
      : types.delete(CompanyConnectionEntityType.mobile_stock_location_sessions);
    enabled
      ? types.add(CompanyConnectionEntityType.mobile_stock_location_session_history)
      : types.delete(CompanyConnectionEntityType.mobile_stock_location_session_history);
    enabled
      ? types.add(CompanyConnectionEntityType.mobile_stock_location_events)
      : types.delete(CompanyConnectionEntityType.mobile_stock_location_events);
    enabled ? types.add(CompanyConnectionEntityType.tags) : types.delete(CompanyConnectionEntityType.tags);
    enabled
      ? types.add(CompanyConnectionEntityType.tag_relation)
      : types.delete(CompanyConnectionEntityType.tag_relation);

    this.types = Array.from(types.values());

    return cloneDeep(this);
  }

  withOrders(enabled: boolean) {
    this.types = this.withProductMasterData(enabled).types;
    this.types = this.withStockLocations(enabled).types;
    this.types = this.withBins(enabled).types;
    this.types = this.withLots(enabled).types;
    this.types = this.withContainers(enabled).types;

    const types = new Set(this.types);
    enabled ? types.add(CompanyConnectionEntityType.orders) : types.delete(CompanyConnectionEntityType.orders);

    this.types = Array.from(types.values());

    return cloneDeep(this);
  }

  withTasks(enabled: boolean) {
    this.types = this.withProductMasterData(enabled).types;
    this.types = this.withStockLocations(enabled).types;
    this.types = this.withBins(enabled).types;
    this.types = this.withLots(enabled).types;
    this.types = this.withContainers(enabled).types;

    const types = new Set(this.types);
    enabled ? types.add(CompanyConnectionEntityType.tasks) : types.delete(CompanyConnectionEntityType.tasks);

    this.types = Array.from(types.values());

    return cloneDeep(this);
  }

  withBins(enabled: boolean) {
    this.types = this.withStockLocations(enabled).types;

    const types = new Set(this.types);
    enabled ? types.add(CompanyConnectionEntityType.bins) : types.delete(CompanyConnectionEntityType.bins);
    enabled ? types.add(CompanyConnectionEntityType.bin_status) : types.delete(CompanyConnectionEntityType.bin_status);

    this.types = Array.from(types.values());

    return cloneDeep(this);
  }

  withProducts(enabled: boolean) {
    this.types = this.withProductMasterData(enabled).types;
    this.types = this.withStockLocations(enabled).types;
    this.types = this.withBins(enabled).types;
    this.types = this.withContainers(enabled).types;
    this.types = this.withLots(enabled).types;

    const types = new Set(this.types);
    enabled ? types.add(CompanyConnectionEntityType.products) : types.delete(CompanyConnectionEntityType.products);

    this.types = Array.from(types.values());

    return cloneDeep(this);
  }

  withLots(enabled: boolean) {
    this.types = this.withProductMasterData(enabled).types;

    const types = new Set(this.types);
    enabled ? types.add(CompanyConnectionEntityType.lots) : types.delete(CompanyConnectionEntityType.lots);

    this.types = Array.from(types.values());

    return cloneDeep(this);
  }

  withContainers(enabled: boolean) {
    this.types = this.withStockLocations(enabled).types;
    this.types = this.withBins(enabled).types;

    const types = new Set(this.types);
    enabled ? types.add(CompanyConnectionEntityType.containers) : types.delete(CompanyConnectionEntityType.containers);
    enabled
      ? types.add(CompanyConnectionEntityType.container_types)
      : types.delete(CompanyConnectionEntityType.container_types);

    this.types = Array.from(types.values());

    return cloneDeep(this);
  }

  withProductTransactions(enabled: boolean) {
    this.types = this.withProductMasterData(enabled).types;
    this.types = this.withStockLocations(enabled).types;
    this.types = this.withBins(enabled).types;
    this.types = this.withContainers(enabled).types;
    this.types = this.withLots(enabled).types;

    const types = new Set(this.types);
    enabled
      ? types.add(CompanyConnectionEntityType.product_transactions)
      : types.delete(CompanyConnectionEntityType.product_transactions);

    this.types = Array.from(types.values());

    return cloneDeep(this);
  }

  withAssets(enabled: boolean) {
    this.types = this.withProductMasterData(enabled).types;

    const types = new Set(this.types);
    enabled ? types.add(CompanyConnectionEntityType.assets) : types.delete(CompanyConnectionEntityType.assets);
    enabled
      ? types.add(CompanyConnectionEntityType.asset_transactions)
      : types.delete(CompanyConnectionEntityType.asset_transactions);

    this.types = Array.from(types.values());

    return cloneDeep(this);
  }
}

export class CreateCompanyConnectionInput extends forCreate(CompanyConnection) {}
export class UpdateCompanyConnectionInput extends forUpdate(CompanyConnection) {}
export class DeleteCompanyConnectionInput extends forDelete(CompanyConnection) {}
